/**
 * Visual projection of the logical dialog stack (layered overlays).
 *
 * The logical stack is the single source of truth for navigation order. This
 * module derives, without touching the DOM, which entries are visible, which
 * one is active, which are collapsed and whether a placeholder is needed.
 */

export type DialogStackEntryType = 'dialog' | 'drillInto'
export type DialogStackPresentation = 'dock' | 'windowed' | 'fullscreen'
export type DialogRenderState = 'active' | 'stacked' | 'hidden'

export type DialogStackProjectionInput = {
  type: DialogStackEntryType
  presentation: DialogStackPresentation
  requiresScrim: boolean
}

export type DialogStackEntryProjection = {
  state: DialogRenderState
  /** Number of visible entries above this one in the same consecutive type group and presentation. */
  depth: number
}

export type DialogStackPlaceholderProjection = {
  hiddenCount: number
  presentation: DialogStackPresentation
  depth: number
}

export type DialogStackProjection = {
  entries: DialogStackEntryProjection[]
  placeholder: DialogStackPlaceholderProjection | null
  scrimVisible: boolean
  /** Visible entries that begin a new dialog-type group above another group. */
  scrimBefore: number[]
}

export const MAX_VISIBLE_DRILL_INTO_SURFACES = 5

/**
 * Project a logical stack (ordered bottom → top) onto its visual representation.
 *
 * Hidden entries always form a contiguous bottom slice so one placeholder can
 * represent them without misrepresenting their logical order.
 */
export function projectDialogStack(entries: readonly DialogStackProjectionInput[], maxVisibleSurfacesPerType = MAX_VISIBLE_DRILL_INTO_SURFACES): DialogStackProjection {
  const hiddenCount = countHiddenEntries(entries, maxVisibleSurfacesPerType)
  const topIndex = entries.length - 1

  const projected = entries.map((entry, index): DialogStackEntryProjection => {
    if (index < hiddenCount) {
      return { state: 'hidden', depth: 0 }
    }

    return {
      state: index === topIndex ? 'active' : 'stacked',
      depth: countGroupDepth(entries, index),
    }
  })

  // The placeholder continues the visual stack of the hidden entries, unless a fullscreen surface
  // occludes them: then it sits directly beneath that surface instead of peeking around it.
  const placeholderPresentation = hiddenCount > 0 && entries[hiddenCount].presentation === 'fullscreen'
    ? 'fullscreen'
    : entries[hiddenCount - 1]?.presentation
  const placeholder = hiddenCount > 0
    ? {
      hiddenCount,
      presentation: placeholderPresentation,
      depth: countVisibleWithPresentation(entries, hiddenCount, placeholderPresentation),
    }
    : null

  return {
    entries: projected,
    placeholder,
    scrimVisible: entries.some(entry => entry.requiresScrim),
    scrimBefore: entries.flatMap((entry, index) => (
      index > hiddenCount && entry.type !== entries[index - 1].type && entry.requiresScrim ? [index] : []
    )),
  }
}

function countHiddenEntries(entries: readonly DialogStackProjectionInput[], maxVisibleSurfacesPerType: number) {
  let cutoff = -1
  const visibleByType = { dialog: 0, drillInto: 0 }

  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]

    visibleByType[entry.type] += 1

    // Older surfaces beyond the per-type limit collapse with everything below them.
    if (visibleByType[entry.type] > maxVisibleSurfacesPerType) {
      cutoff = index
      break
    }

    // Rule B: a fullscreen surface occludes everything beneath it.
    if (entry.presentation === 'fullscreen') {
      cutoff = index - 1
      break
    }
  }

  return cutoff + 1
}

function countVisibleWithPresentation(entries: readonly DialogStackProjectionInput[], fromIndex: number, presentation: DialogStackPresentation) {
  let count = 0

  for (let index = fromIndex; index < entries.length; index += 1) {
    if (entries[index].presentation === presentation) {
      count += 1
    }
  }

  return count
}

function countGroupDepth(entries: readonly DialogStackProjectionInput[], index: number) {
  const entry = entries[index]
  let depth = 0

  for (let above = index + 1; above < entries.length; above += 1) {
    if (entries[above].type !== entry.type) {
      break
    }
    if (entries[above].presentation === entry.presentation) {
      depth += 1
    }
  }

  return depth
}
